/**
 * LMS Operations — Tab 1 "Loan Summary" popup.
 *
 * An eye button sitting beside "Download Payloads" on the loan profile header.
 * It opens a read-only, structured view of the 18-field customer/loan record the
 * business asked for (Cust ID … E-mail ID) and offers JSON + CSV downloads of
 * exactly that record from the top of the dialog.
 *
 * NO NETWORK CALL OF ITS OWN. The data arrives as `loan_summary` on the SAME
 * `LoanProfileView` response the page already fetched, so opening the popup is
 * instant and can never show a figure that disagrees with the header above it.
 *
 * WHY THE FIELD LIST IS IMPORTED RATHER THAN WRITTEN HERE: `LOAN_SUMMARY_FIELDS`
 * (lmsStaffApi.ts, mirroring `LOAN_SUMMARY_FIELDS` in views_staff_ops.py) is the
 * single order+label definition driving the on-screen rows, the JSON key order
 * and the CSV header. Duplicating it per consumer is how an export silently
 * stops matching the screen.
 */
import React from "react";
import { Download, Eye, FileJson, FileSpreadsheet } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { LOAN_SUMMARY_FIELDS, LoanSummary } from "./lmsStaffApi";
import { formatAmount, formatDateTime } from "./formatters";
import { TWO_LINE_BUTTON_CLASS, TwoLineLabel } from "./twoLineButton";

/**
 * Fields rendered as money (`₹`) and as a date-time on screen.
 *
 * ⚠ DISPLAY ONLY. The downloads deliberately carry the RAW backend values (a
 * Decimal string / an ISO timestamp), never these formatted strings: "₹2,76,990.00"
 * is not a number any spreadsheet or downstream system can compute with, and a
 * locale-formatted date is ambiguous between DD/MM and MM/DD.
 */
const MONEY_KEYS: ReadonlySet<keyof LoanSummary> = new Set([
    "sanctioned_loan_amount",
    "amount_disbursed",
]);
const DATETIME_KEYS: ReadonlySet<keyof LoanSummary> = new Set(["date_of_disbursal"]);

const displayValue = (key: keyof LoanSummary, value: LoanSummary[keyof LoanSummary]) => {
    if (value === null || value === undefined || value === "") return "-";
    if (MONEY_KEYS.has(key)) return formatAmount(value as string);
    if (DATETIME_KEYS.has(key)) return formatDateTime(value as string);
    if (key === "roi") return `${value}%`;
    return String(value);
};

/** Triggers a browser download of `blob` under `filename`. */
const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Without this the object URL (and the blob behind it) leaks for the
    // lifetime of the document.
    URL.revokeObjectURL(url);
};

/**
 * Characters that make Excel / Google Sheets treat a cell as a FORMULA.
 * Mirrors `_CSV_INJECTION_PREFIXES` / `_csv_safe()` in views_staff_ops.py —
 * names, bank names and email addresses in this record are all user-supplied,
 * so a value like `=HYPERLINK(...)` opened by a staff member is a real
 * exfiltration vector, not a formatting nicety.
 */
const CSV_INJECTION_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

const csvCell = (value: unknown): string => {
    if (value === null || value === undefined) return '""';
    let text = String(value);
    if (CSV_INJECTION_PREFIXES.some((prefix) => text.startsWith(prefix))) {
        text = `'${text}`;
    }
    // Quote unconditionally and double any embedded quote — the simplest form
    // that is correct for values containing commas, quotes or newlines.
    return `"${text.replace(/"/g, '""')}"`;
};

export interface LoanSummaryDialogProps {
    summary: LoanSummary;
    /** Used only to name the downloaded files. */
    loanId: string;
}

const LoanSummaryDialog: React.FC<LoanSummaryDialogProps> = ({ summary, loanId }) => {
    /**
     * A plain object in LOAN_SUMMARY_FIELDS order, keyed by the backend field
     * names — the payload both downloads are built from. Raw values only (see
     * MONEY_KEYS).
     */
    const orderedRecord = React.useMemo(() => {
        const record: Record<string, unknown> = {};
        LOAN_SUMMARY_FIELDS.forEach(({ key }) => {
            record[key] = summary[key] ?? null;
        });
        return record;
    }, [summary]);

    const handleDownloadJson = () => {
        downloadBlob(
            new Blob([JSON.stringify(orderedRecord, null, 2)], {
                type: "application/json",
            }),
            `loan_summary_${loanId}.json`
        );
    };

    const handleDownloadCsv = () => {
        const header = LOAN_SUMMARY_FIELDS.map(({ label }) => csvCell(label)).join(",");
        const row = LOAN_SUMMARY_FIELDS.map(({ key }) => csvCell(summary[key])).join(",");
        // The UTF-8 BOM leads the file so Excel renders Indian names correctly
        // instead of mojibake — same reason the server-side register export
        // prepends one.
        downloadBlob(
            new Blob([`\uFEFF${header}\n${row}\n`], { type: "text/csv;charset=utf-8;" }),
            `loan_summary_${loanId}.csv`
        );
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-label="View loan summary"
                    title="View loan summary"
                    className={TWO_LINE_BUTTON_CLASS}
                >
                    <Eye className="h-4 w-4" />
                    <TwoLineLabel top="View" bottom="Summary" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {summary.customer_name || "Customer"} — Loan Summary
                    </DialogTitle>
                    <DialogDescription>
                        {loanId} · Read-only. Downloads carry the raw stored values
                        (decimal amounts, ISO timestamps), not the formatting shown
                        below.
                    </DialogDescription>
                </DialogHeader>

                {/* Downloads at the top, per spec */}
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={handleDownloadJson}
                    >
                        <FileJson className="h-4 w-4" /> Download JSON
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={handleDownloadCsv}
                    >
                        <FileSpreadsheet className="h-4 w-4" /> Download CSV
                    </Button>
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400">
                        <Download className="h-3.5 w-3.5" /> {LOAN_SUMMARY_FIELDS.length}{" "}
                        fields
                    </span>
                </div>

                <Separator />

                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                        <tbody>
                            {LOAN_SUMMARY_FIELDS.map(({ key, label }, index) => (
                                <tr
                                    key={key}
                                    className={
                                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                    }
                                >
                                    <th className="w-1/2 px-3 py-2 text-left align-top text-xs font-medium uppercase tracking-wide text-gray-500">
                                        {label}
                                    </th>
                                    <td className="px-3 py-2 align-top text-gray-900 break-words">
                                        {displayValue(key, summary[key])}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LoanSummaryDialog;
